/**
 * Lab Terminal Page - Terminal access for lab environment (with heartbeat)
 */
import React, { useState, useEffect, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import { apiService } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { ChevronLeft, Terminal, Power, Wifi, WifiOff, Loader2, AlertCircle, CheckCircle, BookOpen, Target, Zap } from 'lucide-react';
import SimpleLabTimer from '../components/SimpleLabTimer';
import LabTimer from '../components/LabTimer';
import { toast } from 'sonner';
import { trackFirstLabLead } from '../utils/metaPixel';

const LabTerminal = () => {
  const { course, labId } = useParams();
  const [session, setSession] = useState(null);
  const [status, setStatus] = useState({ running: false });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [timerStarted, setTimerStarted] = useState(false);
  const heartbeatRef = useRef(null);

  // 💓 Heartbeat to keep VM session alive
  const startHeartbeat = () => {
    if (heartbeatRef.current) clearInterval(heartbeatRef.current);
    heartbeatRef.current = setInterval(async () => {
      try {
        if (status.running && course && labId) {
          console.log(`❤️ Sending heartbeat for ${course}/${labId}`);
          await apiService.sendHeartbeat(course, labId);
        }
      } catch (err) {
        console.warn('⚠️ Heartbeat failed:', err);
      }
    }, 60000); // every 1 minute
  };

  const stopHeartbeat = () => {
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
  };

  useEffect(() => {
    checkTerminalStatus();
    // Meta Pixel: Lead — fires ONCE per browser when user opens their first
    // lab terminal page. Deduplicated via localStorage in trackFirstLabLead().
    // This marks the user as a "real-intent" lead for Meta's ad optimisation.
    if (course && labId) {
      trackFirstLabLead(course, labId);
    }
    const checkTimerStatus = async () => {
      try {
        const timerStatus = await apiService.getLabTimerStatus(course, labId);
        if (timerStatus.success && timerStatus.data.timer_active) {
          setTimerStarted(true);
        }
      } catch {
        console.log('ℹ️ No active timer to restore');
      }
    };
    if (course && labId) checkTimerStatus();

    return () => {
      stopHeartbeat();
      if (timerStarted) {
        try {
          apiService.completeLabTimer(course, labId);
          localStorage.removeItem('lab_timer_start');
        } catch (error) {
          console.log('⚠️ Timer completion failed (non-critical):', error);
        }
      }
    };
  }, [course, labId]);

  const checkTerminalStatus = async () => {
    try {
      const statusData = await apiService.getTerminalStatus(course, labId);
      if (statusData.running) {
        setStatus({
          running: true,
          container_id: statusData.vm_id || statusData.container_id
        });
        setLoading(false);
        startHeartbeat(); // start heartbeat

        if (!session && statusData.terminalUrl) {
          let port = statusData.port;
          if (!port && statusData.terminalUrl) {
            const match = statusData.terminalUrl.match(/:(\d+)$/);
            port = match ? parseInt(match[1]) : 30000;
          }
          setSession({
            container_id: statusData.vm_id || statusData.container_id,
            ports: [port],
            terminalUrl: statusData.terminalUrl,
            reused: true
          });
        }
      } else {
        setStatus({ running: false });
        if (session) {
          setSession(null);
          stopHeartbeat();
        }
      }
    } catch (error) {
      console.error('Failed to check terminal status:', error);
    }
  };

  const handleStartTerminal = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await apiService.startTerminal(course, labId);
      const sessionData = {
        container_id: response.container_id,
        ports: response.ports,
        terminalUrl: response.terminalUrl,
        reused: response.reused
      };
      setSession(sessionData);
      window.session = sessionData;
      setStatus({
        running: true,
        container_id: response.container_id,
        port: response.ports[0]
      });
      startHeartbeat(); // start heartbeat
      toast.success(`Terminal ${response.reused ? 'reconnected' : 'started'} on port ${response.ports[0]}`);

      if (!response.reused && !timerStarted) {
        try {
          const timerResponse = await apiService.startLabTimer(course, labId);
          console.log('✅ Timer started automatically:', timerResponse);
          setTimerStarted(true);
          toast.success('Lab timer started! 🚀', { duration: 2000 });
        } catch (timerError) {
          console.error('⚠️ Failed to auto-start timer:', timerError);
        }
      } else if (response.reused) {
        setTimerStarted(true);
      }
      setTimeout(checkTerminalStatus, 1000);
    } catch (error) {
      const message = error.response?.data?.detail || error.message || 'Failed to start terminal';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleStopTerminal = async () => {
    setLoading(true);
    try {
      await apiService.stopTerminal(course, labId);
      setSession(null);
      setStatus({ running: false });
      stopHeartbeat();
      toast.success('Terminal session stopped');
    } catch (error) {
      console.error('Failed to stop terminal:', error);
      toast.error('Failed to stop terminal session');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4" data-testid="lab-terminal-page">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-4">
            <div className="flex items-center gap-3 lg:hidden mb-4">
              <Link to={`/courses/${course}`} className="text-gray-400 hover:text-white bg-slate-800/80 p-3 rounded-xl">
                <ChevronLeft className="h-5 w-5" />
              </Link>
              <div>
                <h1 className="text-xl font-bold text-white">{course} Lab {labId}</h1>
                <p className="text-sm text-emerald-400">Interactive Environment</p>
              </div>
            </div>
            <div data-testid="lab-timer-container">
              <LabTimer course={course} labId={labId} onTimerStart={() => setTimerStarted(true)} />
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3 space-y-6">
            <Card className="bg-white/5 border-white/10 shadow-2xl">
              <CardHeader className="border-b border-white/10">
                <CardTitle className="text-white flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${status.running ? 'bg-emerald-500/20 border border-emerald-400/50' : 'bg-red-500/20 border border-red-400/50'}`}>
                    {status.running ? <Wifi className="h-5 w-5 text-emerald-400" /> : <WifiOff className="h-5 w-5 text-red-400" />}
                  </div>
                  <span className="text-xl">{status.running ? 'Terminal Active' : 'Terminal Inactive'}</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {error && (
                  <Alert className="mb-6 bg-red-500/20 border-red-400/50">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription className="text-red-200 font-medium">{error}</AlertDescription>
                  </Alert>
                )}

                <div className="flex gap-4">
                  {!status.running ? (
                    <Button onClick={handleStartTerminal} disabled={loading} className="bg-emerald-600 hover:bg-emerald-700 text-white" size="lg">
                      {loading && <Loader2 className="mr-2 h-5 w-5 animate-spin" />}
                      <Terminal className="mr-2 h-5 w-5" />
                      Start Terminal
                    </Button>
                  ) : (
                    <Button onClick={handleStopTerminal} disabled={loading} variant="destructive" className="shadow-xl">
                      {loading && <Loader2 className="mr-2 h-5 w-5 animate-spin" />}
                      <Power className="mr-2 h-5 w-5" />
                      Stop Terminal
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {status.running && session?.terminalUrl && (
              <Card className="bg-slate-900 border-slate-600 shadow-2xl overflow-hidden">
                <CardHeader className="border-b border-slate-600 bg-slate-800/80">
                  <CardTitle className="text-green-400 text-sm font-mono flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Terminal className="h-4 w-4" />
                      <span>VM Terminal - {course} Lab {labId}</span>
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <iframe
                    src={session.terminalUrl}
                    className="w-full border-0 bg-black"
                    title={`Terminal for ${course} Lab ${labId}`}
                    style={{ minHeight: '600px', backgroundColor: '#000' }}
                  />
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LabTerminal;

